import React, { useState, useEffect, useRef } from "react";
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from './data/supabaseClient';
import { useTranslation } from 'react-i18next';
import { useToast } from './hooks/ToastContext';
import GoogleSignInButton from "./GoogleSignInButton";
import LanguageSwitcher from "./LanguageSwitcher";
import ParticleSystem from "./ParticleSystem";
import Spinner from './common/Spinner';
import ErrorMessage from './common/ErrorMessage';
import { isValidEmailFormat, isDisposableEmail } from '../utils/disposableEmailDomains';
import './LoginForm.css';

// Import benefit images
import RouteMotif from './common/RouteMotif';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  
  // Form data
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  
  // Check for success message from password reset
  useEffect(() => {
    if (location.state?.message) {
      addToast({
        type: 'success',
        title: t('auth.password_reset_success'),
        description: location.state.message
      });
      
      // Pre-fill email if provided
      if (location.state.email) {
        setLoginEmail(location.state.email);
      }
      
      // Clear the state to prevent showing message again
      window.history.replaceState({}, document.title);
    }
  }, [location.state, addToast, t]);
  
  // Container ref for particle system
  const containerRef = useRef(null);
  const gridRef = useRef(null);
  
  // Mouse position tracking for grid only
  const [gridTransform, setGridTransform] = useState({ x: 0, y: 0 });

  // Form states
  const [showSignup, setShowSignup] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [signupError, setSignupError] = useState(null);

  // Grid mouse movement handler
  useEffect(() => {
    let animationFrameId = null;

    const handleMouseMove = (e) => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);

      animationFrameId = requestAnimationFrame(() => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;
          const xRatio = (e.clientX - rect.left - centerX) / centerX;
          const yRatio = (e.clientY - rect.top - centerY) / centerY;

          // Grid movement - subtle effect
          setGridTransform({
            x: xRatio * 5,
            y: yRatio * 5
          });
        }
      });
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      return () => {
        container.removeEventListener('mousemove', handleMouseMove);
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
      };
    }
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsLoginLoading(true);
    setLoginError(null);
  
    try {
      if (!loginEmail || !loginPassword) {
        setLoginError(t('auth.email_password_required'));
        setIsLoginLoading(false);
        return;
      }
  
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email: loginEmail, 
        password: loginPassword 
      });
  
      if (error) throw error;
  
      const { data: profile } = await supabase
        .from('profiles')
        .select('username, completion_step')
        .eq('id', data.user.id)
        .single();
  
      addToast({
        type: 'success',
        title: t('auth.login_success'),
        description: profile?.username ? 
          t('auth.welcome_back', { name: profile.username }) : 
          t('auth.welcome')
      });
  
      if (!profile?.username) {
        navigate('/complete-profile');
      } else {
        navigate('/home');
      }
    } catch (error) {
      // Handle authentication errors gracefully without console spam
      if (error.message.includes('Invalid login')) {
        setLoginError(t('auth.invalid_credentials'));
      } else if (error.message.includes('rate limit')) {
        setLoginError(t('auth.too_many_attempts'));
      } else {
        setLoginError(t('auth.login_error'));
      }
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleSignUp = async (event) => {
    event.preventDefault();
    setIsSignupLoading(true);
    setSignupError(null);

    try {
      if (!signupEmail || !signupPassword || !signupConfirmPassword) {
        setSignupError(t('auth.fill_all_fields'));
        setIsSignupLoading(false);
        return;
      }
      
      if (signupPassword !== signupConfirmPassword) {
        setSignupError(t('auth.passwords_dont_match'));
        setIsSignupLoading(false);
        return;
      }

      if (signupPassword.length < 6) {
        setSignupError(t('auth.password_too_short'));
        setIsSignupLoading(false);
        return;
      }

      if (!isValidEmailFormat(signupEmail)) {
        setSignupError(t('auth.invalid_email'));
        setIsSignupLoading(false);
        return;
      }

      // Block disposable / temporary email providers (also enforced in the DB).
      if (isDisposableEmail(signupEmail)) {
        setSignupError(t('auth.disposable_email_not_allowed'));
        setIsSignupLoading(false);
        return;
      }

      const { data, error } = await supabase.auth.signUp({
        email: signupEmail,
        password: signupPassword,
        options: {
          data: {
            email: signupEmail,
          }
        }
      });

      if (error) throw error;

      addToast({
        type: 'success',
        title: t('auth.signup_success_title'),
        description: t('auth.signup_successful')
      });
      
      setSignupEmail('');
      setSignupPassword('');
      setSignupConfirmPassword('');
      await supabase.auth.signOut();

      setShowSignup(false);
      setLoginEmail(signupEmail);

    } catch (error) {
      console.error("Error during signup:", error);
      
      if (error.message?.includes('already registered')) {
        setSignupError(t('auth.email_already_exists'));
      } else if (error.message?.includes('DISPOSABLE_EMAIL_NOT_ALLOWED')) {
        setSignupError(t('auth.disposable_email_not_allowed'));
      } else {
        setSignupError(error.message || t('auth.signup_error'));
      }
    } finally {
      setIsSignupLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!loginEmail) {
      setLoginError(t('auth.enter_email_for_reset'));
      return;
    }

    setIsLoginLoading(true);

    try {
      // Get the appropriate redirect URL based on environment
      const isDevelopment = window.location.hostname === 'localhost';
      const redirectUrl = isDevelopment
        ? 'http://localhost:3000/reset-password'
        : 'https://wayshare.app/reset-password';

      // Get current language from i18next
      const currentLanguage = localStorage.getItem('language') || 'lv';

      console.log('Sending password reset email with redirect to:', redirectUrl, 'in language:', currentLanguage);

      const { error } = await supabase.auth.resetPasswordForEmail(loginEmail, {
        redirectTo: redirectUrl,
        data: {
          language: currentLanguage
        }
      });
      
      if (error) throw error;
      
      addToast({
        type: 'info',
        title: t('auth.password_reset_sent'),
        description: t('auth.check_email_for_reset')
      });
    } catch (error) {
      console.error("Password reset error:", error);
      setLoginError(t('auth.password_reset_error'));
    } finally {
      setIsLoginLoading(false);
    }
  };

  return (
    <div ref={containerRef} className="enhanced-login-page">
      {/* Language switcher — native app only (web login has it in the header).
          Gives first-time app users a way to pick their language pre-login. */}
      <div className="native-login-lang">
        <LanguageSwitcher />
      </div>

      {/* Landing Benefits Section */}
      <div className="landing-benefits-section">
        <div className="benefits-container">
          <motion.h2 
            className="benefits-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {t('landing.discover_benefits') || 'Discover the Benefits of ConRide'}
          </motion.h2>
          
          <motion.p 
            className="benefits-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t('landing.join_community') || 'Join thousands of users who save money and help the environment'}
          </motion.p>

          <div className="benefits-grid">
            <motion.div 
              className="benefit-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="benefit-image">
                <RouteMotif variant="driver" />
              </div>
              <h3>{t('benefits.driver_title') || 'As a Driver'}</h3>
              <ul>
                <li>{t('benefits.driver_point1') || 'Cover fuel costs'}</li>
                <li>{t('benefits.driver_point2') || 'Meet new people'}</li>
                <li>{t('benefits.driver_point3') || 'Help the environment'}</li>
              </ul>
            </motion.div>

            <motion.div 
              className="benefit-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="benefit-image">
                <RouteMotif variant="passenger" />
              </div>
              <h3>{t('benefits.passenger_title') || 'As a Passenger'}</h3>
              <ul>
                <li>{t('benefits.passenger_point1') || 'Save up to 70%'}</li>
                <li>{t('benefits.passenger_point2') || 'Travel comfortably'}</li>
                <li>{t('benefits.passenger_point3') || 'Verified drivers'}</li>
              </ul>
            </motion.div>

            <motion.div 
              className="benefit-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="benefit-image">
                <RouteMotif variant="package" />
              </div>
              <h3>{t('benefits.package_title') || 'Package Delivery'}</h3>
              <ul>
                <li>{t('benefits.package_point1') || 'Fast delivery'}</li>
                <li>{t('benefits.package_point2') || 'Affordable prices'}</li>
                <li>{t('benefits.package_point3') || 'Track your package'}</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Animated background grid */}
      <div className="animated-bg-grid">
        <div 
          ref={gridRef}
          className="grid-pattern" 
          style={{
            transform: `translate(${gridTransform.x}px, ${gridTransform.y}px) scale(${1 + Math.abs(gridTransform.x + gridTransform.y) * 0.001})`
          }}
        />
      </div>

      {/* Particle System - Enhanced with more particles */}
      <ParticleSystem 
        particleCount={30} 
        mouseInfluence={0}
        containerRef={containerRef} 
      />

      {/* Logo + tagline. The wordmark shows only in the native app (on web the
          header already carries the WayShare logo). */}
      <div className="conride-logo-container">
        <div className="native-login-logo" aria-hidden="true">
          Way<span>Share</span>
        </div>
        <p className="logo-tagline">
          {t('app.tagline') || 'Connect • Ride • Deliver'}
        </p>
      </div>



      {/* Login/Signup form - centered */}
      <div id="login-form" className="auth-form-container">
        <div className="auth-form-box">
          {!showSignup ? (
            <>
              <form onSubmit={handleLogin}>
                <div className="form-field">
                  <input
                    type="email"
                    placeholder={t('auth.email_placeholder')}
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="enhanced-input"
                  />
                </div>
                
                <div className="form-field">
                  <input
                    type="password"
                    placeholder={t('auth.password_placeholder')}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="enhanced-input"
                  />
                </div>
                
                {loginError && (
                  <ErrorMessage message={loginError} type="error" variant="block" />
                )}
                
                <button
                  type="submit"
                  className="enhanced-button primary"
                  disabled={isLoginLoading}
                >
                  {isLoginLoading ? (
                    <div className="button-label-with-spinner">
                      <Spinner size="small" color="var(--background-colorD)" showText={false} />
                      <span>{t('auth.signing_in')}</span>
                    </div>
                  ) : (
                    t('buttons.login')
                  )}
                </button>
              </form>
              
              <button 
                onClick={handleForgotPassword}
                className="link-button"
                disabled={isLoginLoading}
              >
                {t('auth.forgot_password')}
              </button>
              
              <div className="divider">
                <span>{t('auth.or_continue_with')}</span>
              </div>
              
              <GoogleSignInButton />
              
              <p className="switch-form-text">
                {t("auth.dont_have_account")}
                <button
                  onClick={() => setShowSignup(true)}
                  className="link-button inline"
                >
                  {t('buttons.signup')}
                </button>
              </p>
            </>
          ) : (
            <>
              <h2>{t('auth.signup_form')}</h2>
              
              <form onSubmit={handleSignUp}>
                <div className="form-field">
                  <input
                    type="email"
                    placeholder={t('auth.email_placeholder')}
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    className="enhanced-input"
                  />
                </div>
                
                <div className="form-field">
                  <input
                    type="password"
                    placeholder={t('auth.password_placeholder')}
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    className="enhanced-input"
                  />
                </div>
                
                <div className="form-field">
                  <input
                    type="password"
                    placeholder={t('auth.confirm_password')}
                    value={signupConfirmPassword}
                    onChange={(e) => setSignupConfirmPassword(e.target.value)}
                    className="enhanced-input"
                  />
                </div>
                
                {signupError && (
                  <ErrorMessage message={signupError} type="error" variant="block" />
                )}
                
                <button
                  type="submit"
                  className="enhanced-button primary"
                  disabled={isSignupLoading}
                >
                  {isSignupLoading ? (
                    <div className="button-label-with-spinner">
                      <Spinner size="small" color="var(--background-colorD)" showText={false} />
                      <span>{t('auth.signing_up')}</span>
                    </div>
                  ) : (
                    t('buttons.signup')
                  )}
                </button>
              </form>
              
              <p className="switch-form-text">
                {t('auth.already_have_account')}
                <button
                  onClick={() => setShowSignup(false)}
                  className="link-button inline"
                >
                  {t('auth.back_to_login')}
                </button>
              </p>
            </>
          )}
        </div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div
        className="scroll-indicator"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="scroll-wheel">
          <div className="scroll-dot"></div>
        </div>
      </motion.div>
    </div>
  );
}

export default Login;
